import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DialogBox from '../common/DialogBox';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { Party, PartySodaDetailResponse, PartySodaTransactionDetail, Sodo } from '../../shared/interfaces';
import { GameType } from '../../shared/enume';
import LagayaaKhadhaComponent from '../game/LagayaKhadha';
import { PencilIcon } from '@heroicons/react/24/outline';
import {
  calculateSodaAmount,
  calculateSodaAmountBeforeCommission,
  getSessionSodaWinLossDisplayAtRun,
  MAX_RUN_FOR_SESSION,
  parseLKToRunAndType,
} from '../../utils/sodaCalculations';
import { formatDate, formatDateOnly } from '../../utils/util';
import { buildInitialToTeamMap, buildValidFirstChars } from '../../utils/teamInitials';
import { isCashParty } from '../../utils/cashParty';
import { useAuthorization } from '../../auth/useAuthorization';
import { Role } from '../../auth/roles';
import { useAuth } from '../../context/AuthContext';

interface PartySodaTransactionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: number | string;
  partyId: number | string;
  /** When omitted, the detail call returns every soda for this party and match. */
  isPartnerSoda?: boolean;
  gameDetails?: {
    gameType?: string;
    teamA?: string;
    teamB?: string;
    teamC?: string;
    /** Session: match run for run-wise win/loss (same scale as SessionGameBoard Runs list). */
    sessionMatchRun?: number;
    /** Session: winning team label for header. */
    sessionWinnerTeamName?: string;
    isGameOver?: boolean;
  } | null;
  /** Fired after a soda tally. Trial balance uses this to check or uncheck the party row. */
  onConfirmationChanged?: (result: {
    gameId: number;
    allSodasConfirmed: boolean;
    trialBalanceConfirmed?: boolean;
  }) => void;
}

// Types for API response
interface RawSodaItem {
  id?: number;
  sodaId?: number;
  firstChar?: string;
  /** Session soda run count (API may use Runs / runs) */
  runs?: number | string;
  Runs?: number | string;
  lk?: string;
  amount?: number | string;
  rate?: number | string;
  teamName?: string;
  team?: string;
  confirmed?: boolean;
  isConfirmed?: boolean;
  gameType?: string;
  isPartnerSoda?: boolean;
  parentId?: number | string;
  ParentId?: number | string;
  parentSodaId?: number | string;
  gameWiseSodaSequence?: number;
  GameWiseSodaSequence?: number;
  dateTime?: string;
  DateTime?: string;
  createdAt?: string;
  CreatedAt?: string;
  commissionSnapshot?: string | {
    Partners?: any | null;
    PartyCommission?: {
      ThirdPartyId?: number | null;
      TestPartyCommission?: number | null;
      OneDayPartyCommission?: number | null;
      SessionPartyCommission?: number | null;
      TestThirdPartyCommission?: number | null;
      OneDayThirdPartyCommission?: number | null;
      SessionThirdPartyCommission?: number | null;
    } | null;
  };
}

interface RawTransactionItem {
  id?: number;
  transactionId?: number;
  sodaId?: number;
  rowNumber?: number;
  runs?: number | string;
  Runs?: number | string;
  sequence?: number;
  serialNumber?: number;
  gameWiseSodaSequence?: number;
  GameWiseSodaSequence?: number;
  soda?: RawSodaItem;
  Soda?: RawSodaItem;
  teamAWinNet?: number | string;
  teamAWinCredit?: number | string;
  teamAWinDebit?: number | string;
  teamBWinNet?: number | string;
  teamBWinCredit?: number | string;
  teamBWinDebit?: number | string;
  teamAWin?: number | string;
  teamA?: number | string;
  a?: number | string;
  teamAWinning?: number | string;
  credit?: number | string;
  teamACredit?: number | string;
  teamBWin?: number | string;
  teamB?: number | string;
  b?: number | string;
  teamBWinning?: number | string;
  debit?: number | string;
  teamBDebit?: number | string;
  teamCWin?: number | string;
  teamC?: number | string;
  c?: number | string;
  runningTeamA?: number | string;
  runningA?: number | string;
  runningCredit?: number | string;
  runningTeamB?: number | string;
  runningB?: number | string;
  runningDebit?: number | string;
  runningTeamC?: number | string;
  runningC?: number | string;
  confirmed?: boolean;
  isConfirmed?: boolean;
  team?: string;
  teamName?: string;
  isPartnerSoda?: boolean;
  IsPartnerSoda?: boolean;
  parentId?: number | string;
  ParentId?: number | string;
  parentSodaId?: number | string;
}

/** When `winningTeam` is numeric (e.g. "49"), API uses it as session match run for win/loss — not a team name. */
const parseWinningTeamAsSessionMatchRun = (winningTeam: unknown): number | undefined => {
  if (winningTeam === undefined || winningTeam === null || winningTeam === '') return undefined;
  const s = String(winningTeam).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

interface RawApiResponse {
  gameId?: number;
  matchup?: string;
  gameDate?: string;
  /** Root-level match run (some APIs); session win/loss may use `winningTeam` instead. */
  runs?: number | string;
  game?: {
    id?: number;
    teamA?: string;
    teamB?: string;
    teamC?: string;
    gameType?: string;
    matchDate?: string;
    currentRun?: number | string;
  };
  partyId?: number;
  party?: {
    id?: number;
    code?: string;
    name?: string;
  };
  partyCode?: string;
  partyName?: string;
  isSelfPartner?: boolean;
  IsSelfPartner?: boolean;
  code?: string;
  name?: string;
  gameType?: string;
  teamA?: string;
  teamAName?: string;
  teamB?: string;
  teamBName?: string;
  teamC?: string;
  teamCName?: string;
  teams?: {
    teamA?: string;
    teamB?: string;
    teamC?: string;
  };
  gameDetails?: {
    teamA?: string;
    teamB?: string;
    teamC?: string;
  };
  matchDate?: string;
  date?: string;
  partyTotalTeamA?: number | string;
  totalTeamA?: number | string;
  partyTotalTeamB?: number | string;
  totalTeamB?: number | string;
  partyTotalTeamC?: number | string;
  totalTeamC?: number | string;
  transactions?: RawTransactionItem[];
  data?: RawTransactionItem[];
  items?: RawTransactionItem[];
  totals?: {
    teamA?: number | string;
    totalTeamA?: number | string;
    teamAWinNet?: number | string;
    teamB?: number | string;
    totalTeamB?: number | string;
    teamBWinNet?: number | string;
    teamC?: number | string;
    totalTeamC?: number | string;
  };
  sessionMatchRun?: number | string;
  currentRun?: number | string;
  targetRun?: number | string;
  sessionWinnerTeamName?: string;
  winnerTeamName?: string;
  winningTeam?: string;
  isGameOver?: boolean;
  IsGameOver?: boolean;
}

interface ApiError {
  error?: string;
  message?: string;
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
    };
  };
}

const formatAmount = (value?: number | null) => {
  if (value === undefined || value === null) return '0.00';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatGameType = (type?: string) => {
  switch (type?.toLowerCase()) {
    case GameType.oneday:
      return 'One Day';
    case GameType.session:
      return 'Session';
    case GameType.test:
      return 'Test';
    default:
      return type ?? 'Match';
  }
};

const getNetClass = (value?: number | null) => {
  if (value === undefined || value === null) return 'text-slate-600 dark:text-slate-300';
  if (value > 0) return 'text-emerald-600 dark:text-emerald-400';
  if (value < 0) return 'text-rose-600 dark:text-rose-400';
  return 'text-slate-900 dark:text-slate-100';
};

/** Partner commission *child* row (has parent soda): no HarCut. Parent sodas still get HarCut. */
const isPartnerSodaChildRow = (t: Pick<PartySodaTransactionDetail, 'isPartnerSoda' | 'parentId'>): boolean =>
  t.isPartnerSoda === true && t.parentId != null && Number(t.parentId) > 0;

/** Party HarCut from `/api/Soda/detail`: when winning (pre-commission P&L), show soda stake × harCut ÷ 100000. Skipped for partner *child* rows only. */
const formatHarCutCell = (
  sodaStakeAmount: number,
  partyHarCut: number | undefined,
  teamWinValueBeforeCommission: number,
  skipForPartnerChild?: boolean,
): string => {
  if (skipForPartnerChild) return '—';
  const h = partyHarCut ?? 0;
  if (h === 0 || teamWinValueBeforeCommission <= 0) return '—';
  return formatAmount((sodaStakeAmount * h) / 100000);
};

const harCutCellClass = (
  sodaStakeAmount: number,
  partyHarCut: number | undefined,
  teamWinValueBeforeCommission: number,
  isPartnerSodaRow?: boolean,
) => {
  if (isPartnerSodaRow) return 'text-slate-400 dark:text-slate-500';
  const h = partyHarCut ?? 0;
  if (h === 0 || teamWinValueBeforeCommission <= 0) return 'text-slate-400 dark:text-slate-500';
  return getNetClass((sodaStakeAmount * h) / 100000);
};

const normalizeNumber = (value: unknown): number => {
  if (value === undefined || value === null || value === '') return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

/** Session: real run count from API (`runs` / `Runs`) or numeric `firstChar` when LK is built as team letter + /K|L. */
const extractSessionRunsFromRaw = (item: RawTransactionItem): number | undefined => {
  const raw =
    (item.soda as { runs?: unknown; Runs?: unknown }).runs ??
    (item.soda as { runs?: unknown; Runs?: unknown }).Runs ??
    (item as { runs?: unknown; Runs?: unknown }).runs ??
    (item as { runs?: unknown; Runs?: unknown }).Runs;
  if (raw !== undefined && raw !== null && `${raw}`.trim() !== '') {
    const n = normalizeNumber(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  const fc = item.soda?.firstChar;
  if (fc != null && /^\d+$/.test(String(fc).trim())) {
    return normalizeNumber(fc);
  }
  return undefined;
};

/**
 * Test / One Day: show LK as returned (uppercase). Session: `runs/L` or `runs/K` (e.g. `50/L`).
 * Prefer `runs` from API when present for session rows.
 */
const formatLkColumnValue = (
  lk: string | undefined,
  gameType: string | undefined,
  runs?: number | null,
): string => {
  if (!lk || !lk.trim()) return '—';
  const gt = (gameType ?? '').toLowerCase();
  if (gt !== GameType.session) {
    return lk.toUpperCase();
  }
  const { runNumber, type } = parseLKToRunAndType(lk);
  const run = runs != null && Number.isFinite(runs) ? runs : runNumber;
  return `${run}/${type}`;
};

/** Session: Sodo with full `runs/L|K` LK for `calculateTotalRunWiseWinLoss` (SessionGameBoard / RunsAmountList). */
const buildSessionSodoForRunWise = (
  transaction: PartySodaTransactionDetail,
  gid: number | string,
  partyCode: string,
  gameType: string | undefined,
  isPartnerSodaFlag: boolean,
): Sodo => {
  const { runNumber, type } = parseLKToRunAndType(transaction.lk || '');
  const run =
    transaction.runs != null && Number.isFinite(transaction.runs) ? transaction.runs : runNumber;
  const LK = `${run}/${type}`;
  return {
    id: transaction.id,
    LK,
    team: transaction.team ?? '',
    gameId: typeof gid === 'number' ? gid : parseInt(String(gid), 10) || 0,
    amount: transaction.amount,
    rate: transaction.rate,
    party: partyCode,
    createdAt: new Date().toISOString(),
    user: { id: 0, name: '', username: '' },
    gameType: gameType ?? GameType.session,
    commissionSnapshot: transaction.commissionSnapshot,
    isPartnerSoda: transaction.isPartnerSoda ?? isPartnerSodaFlag,
  };
};

/** Same pipeline as SessionGameBoard: `getSessionSodaWinLossDisplayAtRun` → `calculateTotalRunWiseWinLoss([sodo], true)`. */
const sessionWinLossAtMatchRun = (
  transaction: PartySodaTransactionDetail,
  matchRun: number,
  gid: number | string,
  partyCode: string,
  gameType: string | undefined,
  isPartnerSodaFlag: boolean,
): number => {
  const sodo = buildSessionSodoForRunWise(transaction, gid, partyCode, gameType, isPartnerSodaFlag);
  return getSessionSodaWinLossDisplayAtRun(sodo, matchRun);
};

const PartySodaTransactionsDialog: React.FC<PartySodaTransactionsDialogProps> = ({
  isOpen,
  onClose,
  gameId,
  partyId,
  isPartnerSoda,
  gameDetails,
  onConfirmationChanged,
}) => {
  const { hasRole } = useAuthorization();
  const { userConfig, updateUserConfig } = useAuth();
  const isPartnerUser = hasRole(Role.PARTNER);
  const [savingPartnerView, setSavingPartnerView] = useState(false);
  const partnerSodaFlag = isPartnerSoda === true;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PartySodaDetailResponse | null>(null);
  const [updatingTransactionId, setUpdatingTransactionId] = useState<number | null>(null);
  const [editingSoda, setEditingSoda] = useState<Sodo | null>(null);
  const [parties, setParties] = useState<Party[]>([]);
  const hasChangesRef = useRef(false);


  const normalizeTransaction = (item: RawTransactionItem, index: number, teamALabel: string, teamBLabel: string, teamCLabel?: string): PartySodaTransactionDetail => {
    const soda = item.soda ?? item.Soda ?? {};
    const fc = soda.firstChar ?? '';
    const lkPart = soda.lk ?? '';
    let lk = fc && lkPart ? `${fc}/${lkPart}` : lkPart || fc || '';
    lk = lk.toUpperCase();

    const amount = normalizeNumber(soda.amount);
    const rate = normalizeNumber(soda.rate);
    const transactionTeam = soda.team ?? item.team ?? '';
    
    let teamAWin = normalizeNumber(
      item.teamAWinNet ??
        item.teamAWin ??
        item.teamA ??
        item.a ??
        item.teamAWinning ??
        item.credit ??
        item.teamACredit ??
        0,
    );

    let teamBWin = normalizeNumber(
      item.teamBWinNet ??
        item.teamBWin ??
        item.teamB ??
        item.b ??
        item.teamBWinning ??
        item.debit ??
        item.teamBDebit ??
        0,
    );
    
    // Always calculate from LK, amount, and rate if we have those values
    // This ensures we have values even if API doesn't provide them
    // Use the same calculation logic as GameBoard.tsx via utility function
    if (lk && amount > 0 && rate > 0) {
      // Create a Sodo-like object from RawSodaItem for calculateSodaAmount
      // Try multiple sources for gameType with better fallback
      const gameTypeFromSoda = soda?.gameType;
      const gameTypeFromDetails = gameDetails?.gameType;
      const gameTypeFromData = data?.gameType;
      const resolvedGameType = gameTypeFromSoda || gameTypeFromDetails || gameTypeFromData;
      
      // Log warning if gameType is missing
      if (!resolvedGameType) {
        console.warn(`[normalizeTransaction] gameType is missing for transaction ${index + 1}:`, {
          transactionId: item.id ?? item.transactionId,
          sodaId: item.sodaId,
          'item.soda?.gameType': gameTypeFromSoda,
          'gameDetails?.gameType': gameTypeFromDetails,
          'data?.gameType': gameTypeFromData,
          gameId: gameId,
        });
      }
      
      const sodoLike = {
        id: item.id ?? item.transactionId ?? index + 1,
        LK: lk,
        team: transactionTeam || '',
        gameId: typeof gameId === 'number' ? gameId : parseInt(gameId as string) || 0,
        amount,
        rate,
        party: '', // Not needed for calculation
        createdAt: new Date().toISOString(), // Not needed for calculation
        user: { id: 0, name: '', username: '' }, // Not needed for calculation
        gameType: resolvedGameType, // Use resolved gameType (may be undefined)
        commissionSnapshot: soda?.commissionSnapshot,
      };
      const calculatedTeamA = calculateSodaAmount(sodoLike, teamALabel);
      const calculatedTeamB = calculateSodaAmount(sodoLike, teamBLabel);
      // Always use calculated values when available
      teamAWin = calculatedTeamA;
      teamBWin = calculatedTeamB;
    } else {
      console.warn(`❌ Cannot calculate values for transaction ${index + 1}:`, {
        lk,
        amount,
        rate,
        hasLk: !!lk,
        hasAmount: amount > 0,
        hasRate: rate > 0,
        'API teamAWin': teamAWin,
        'API teamBWin': teamBWin,
      });
    }
    
    const teamCWin = item.teamCWin !== undefined || item.teamC !== undefined || item.c !== undefined
      ? normalizeNumber(item.teamCWin ?? item.teamC ?? item.c ?? 0)
      : undefined;
    
    // Extract confirmed status from multiple possible locations (camelCase, PascalCase, nested soda)
    const rawConfirmed =
      item.confirmed ??
      item.isConfirmed ??
      soda?.confirmed ??
      soda?.isConfirmed ??
      (item as any).Confirmed ??
      (item as any).IsConfirmed ??
      (soda as any)?.Confirmed ??
      (soda as any)?.IsConfirmed;
    const confirmed = rawConfirmed === true || rawConfirmed === 'true' || rawConfirmed === 1;
    
    // Extract the actual soda ID from multiple possible locations
    // This is the ID that should be used for API calls (not the sequence number)
    const sodaId =
      item.sodaId ??
      soda?.id ??
      soda?.sodaId ??
      item.id ??
      item.transactionId ??
      index + 1;

    const gameWiseSodaSequenceRaw =
      soda.gameWiseSodaSequence ?? soda.GameWiseSodaSequence;
    const gameWiseSodaSequenceParsed =
      gameWiseSodaSequenceRaw !== undefined && gameWiseSodaSequenceRaw !== null
        ? Number(gameWiseSodaSequenceRaw)
        : NaN;
    const gameWiseSodaSequence = Number.isFinite(gameWiseSodaSequenceParsed)
      ? gameWiseSodaSequenceParsed
      : undefined;

    const sodaDateTimeRaw =
      soda.dateTime ??
      soda.DateTime ??
      soda.createdAt ??
      soda.CreatedAt ??
      (item as { dateTime?: string; DateTime?: string; createdAt?: string }).dateTime ??
      (item as { DateTime?: string }).DateTime ??
      (item as { createdAt?: string }).createdAt;
    const sodaDateTime =
      sodaDateTimeRaw !== undefined && sodaDateTimeRaw !== null && `${sodaDateTimeRaw}`.trim() !== ''
        ? String(sodaDateTimeRaw)
        : undefined;

    const result = {
      id: sodaId, // Use actual soda ID, not sequence number
      gameWiseSodaSequence,
      lk: lk.toUpperCase(),
      runs: extractSessionRunsFromRaw(item),
      amount,
      rate,
      teamAWin,
      teamBWin,
      teamCWin,
      runningTeamA: normalizeNumber(item.runningTeamA ?? item.runningA ?? item.runningCredit ?? 0),
      runningTeamB: normalizeNumber(item.runningTeamB ?? item.runningB ?? item.runningDebit ?? 0),
      runningTeamC: item.runningTeamC !== undefined ? normalizeNumber(item.runningTeamC ?? item.runningC ?? 0) : undefined,
      confirmed,
      createdAt: sodaDateTime,
      // Store team and commissionSnapshot for use in getAmount (matching GameBoard's sodo structure)
      team: transactionTeam,
      commissionSnapshot: soda?.commissionSnapshot,
      ...(typeof soda.isPartnerSoda === 'boolean'
        ? { isPartnerSoda: soda.isPartnerSoda }
        : typeof item.isPartnerSoda === 'boolean'
          ? { isPartnerSoda: item.isPartnerSoda }
          : typeof item.IsPartnerSoda === 'boolean'
            ? { isPartnerSoda: item.IsPartnerSoda }
            : {}),
      ...(() => {
        const pidRaw =
          soda.parentId ??
          soda.ParentId ??
          soda.parentSodaId ??
          item.parentId ??
          item.ParentId ??
          item.parentSodaId;
        if (pidRaw === undefined || pidRaw === null || `${pidRaw}`.trim() === '') return {};
        const n = normalizeNumber(pidRaw);
        return n > 0 ? { parentId: n } : {};
      })(),
    };
    
    return result;
  };

  const normalizeResponse = (response: RawApiResponse): PartySodaDetailResponse => {
    const transactions = Array.isArray(response.transactions) 
      ? response.transactions 
      : Array.isArray(response.data) 
        ? response.data 
        : Array.isArray(response.items)
          ? response.items
          : [];
    
    // Extract team names from various possible locations
    const teamA = response.teamA ?? 
                  response.teamAName ?? 
                  response.teams?.teamA ?? 
                  response.game?.teamA ?? 
                  response.gameDetails?.teamA ??
                  'Team A';
    
    const teamB = response.teamB ?? 
                  response.teamBName ?? 
                  response.teams?.teamB ?? 
                  response.game?.teamB ?? 
                  response.gameDetails?.teamB ??
                  'Team B';
    
    const teamC = response.teamC ?? 
                  response.teamCName ?? 
                  response.teams?.teamC ?? 
                  response.game?.teamC ?? 
                  response.gameDetails?.teamC;
    
    const matchRunFromWinningTeam = parseWinningTeamAsSessionMatchRun(response.winningTeam);
    const rawSessionRun =
      matchRunFromWinningTeam !== undefined
        ? matchRunFromWinningTeam
        : response.sessionMatchRun ??
          response.runs ??
          response.game?.currentRun ??
          response.currentRun ??
          response.targetRun;
    const sessionMatchRunParsed =
      rawSessionRun !== undefined && rawSessionRun !== null && `${rawSessionRun}`.trim() !== ''
        ? normalizeNumber(rawSessionRun)
        : undefined;

    const winningTeamStr =
      response.winningTeam !== undefined && response.winningTeam !== null
        ? String(response.winningTeam).trim()
        : '';
    const winningTeamLooksLikeRun = /^\d+(\.\d+)?$/.test(winningTeamStr);

    let sessionWinnerTeamName = response.sessionWinnerTeamName ?? response.winnerTeamName;
    if (!sessionWinnerTeamName && response.matchup) {
      sessionWinnerTeamName = response.matchup;
    }
    if (!sessionWinnerTeamName && winningTeamStr && !winningTeamLooksLikeRun) {
      sessionWinnerTeamName = winningTeamStr;
    }

    const totals = response.totals as
      | {
          teamA?: number | string;
          totalTeamA?: number | string;
          teamAWinNet?: number | string;
          teamB?: number | string;
          totalTeamB?: number | string;
          teamBWinNet?: number | string;
          teamC?: number | string;
          totalTeamC?: number | string;
        }
      | undefined;

    const partyObj = response.party as { harCut?: unknown; HarCut?: unknown } | undefined;
    const harCutRaw =
      (response as { harCut?: unknown; HarCut?: unknown }).harCut ??
      (response as { HarCut?: unknown }).HarCut ??
      partyObj?.harCut ??
      partyObj?.HarCut;
    const harCut =
      harCutRaw !== undefined && harCutRaw !== null && `${harCutRaw}`.trim() !== ''
        ? normalizeNumber(harCutRaw)
        : undefined;

    return {
      gameId: response.gameId ?? response.game?.id ?? 0,
      partyId: response.partyId ?? response.party?.id ?? 0,
      partyCode: response.partyCode ?? response.party?.code ?? response.code ?? '',
      partyName: response.partyName ?? response.party?.name ?? response.name ?? '',
      isSelfPartner: response.isSelfPartner === true || response.IsSelfPartner === true,
      harCut,
      gameType: response.gameType ?? response.game?.gameType ?? '',
      teamA,
      teamB,
      teamC,
      matchDate: response.matchDate ?? response.date ?? response.gameDate ?? response.game?.matchDate,
      partyTotalTeamA: normalizeNumber(
        response.partyTotalTeamA ?? response.totalTeamA ?? totals?.teamAWinNet,
      ),
      partyTotalTeamB: normalizeNumber(
        response.partyTotalTeamB ?? response.totalTeamB ?? totals?.teamBWinNet,
      ),
      partyTotalTeamC: response.partyTotalTeamC !== undefined ? normalizeNumber(response.partyTotalTeamC ?? response.totalTeamC) : undefined,
      transactions: transactions.map((item: RawTransactionItem, index: number) => normalizeTransaction(item, index, teamA, teamB, teamC)),
      totals: {
        teamA: normalizeNumber(
          totals?.teamA ?? totals?.totalTeamA ?? totals?.teamAWinNet ?? response.totalTeamA,
        ),
        teamB: normalizeNumber(
          totals?.teamB ?? totals?.totalTeamB ?? totals?.teamBWinNet ?? response.totalTeamB,
        ),
        teamC:
          totals?.teamC !== undefined
            ? normalizeNumber(totals?.teamC ?? totals?.totalTeamC ?? response.totalTeamC)
            : undefined,
      },
      sessionMatchRun: sessionMatchRunParsed,
      sessionWinnerTeamName: sessionWinnerTeamName?.trim() || undefined,
      isGameOver: response.isGameOver === true || response.IsGameOver === true,
    };
  };

  const loadData = useCallback(() => {
    if (!gameId || !partyId) {
      console.warn('Missing required parameters:', { gameId, partyId });
      setError('Missing game ID or party ID');
      return;
    }
    
    setError(null);
    setLoading(true);
    
    const apiUrl = apiRoutes.sodas.partyDetail(gameId, partyId, isPartnerSoda);
    
    apiService
      .get<RawApiResponse>(apiUrl)
      .then((response) => {
        
        // Handle case where response might be wrapped in a data property
        const responseData = (response as RawApiResponse & { data?: RawApiResponse })?.data ?? response;
        
        if (!responseData) {
          throw new Error('Empty response from API');
        }
        
        const normalized = normalizeResponse(responseData);
        const gid =
          normalized.gameId ||
          (typeof gameId === 'number' ? gameId : parseInt(String(gameId), 10) || 0);
        const pid =
          normalized.partyId ||
          (typeof partyId === 'number' ? partyId : parseInt(String(partyId), 10) || 0);
        if (gid) normalized.gameId = gid;
        if (pid) normalized.partyId = pid;

        if (!normalized.transactions || normalized.transactions.length === 0) {
          console.warn('No transactions found in response');
        }

        setData(normalized);
      })
      .catch((err: unknown) => {
        const apiError = err as ApiError;
        
        // apiService.get throws error.response.data for axios errors
        // So err might be the error data object directly, or have error/message properties
        const errorMessage = 
          apiError?.error ||
          apiError?.message ||
          apiError?.response?.data?.error ||
          apiError?.response?.data?.message ||
          (typeof err === 'string' ? err : null) ||
          'Unable to load party soda transactions.';
        
        setError(errorMessage);
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [gameId, partyId, isPartnerSoda]);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, loadData]);

  useEffect(() => {
    if (!isOpen) return;

    apiService
      .post<{ data?: Party[] }>(apiRoutes.parties.lite, {
        page: 1,
        pageSize: 10000,
        orderBy: 'partyCode',
        orderDirection: 'asc',
      })
      .then((response) => {
        const list = (response as { data?: Party[] })?.data ?? [];
        setParties(list.filter((party) => !isCashParty(party)));
      })
      .catch((err) => {
        console.error('Failed to load parties for soda edit form:', err);
        setParties([]);
      });
  }, [isOpen]);

  const handleConfirmToggle = useCallback(
    async (transaction: PartySodaTransactionDetail, confirmed: boolean) => {
      if (!gameId) return;
      setUpdatingTransactionId(transaction.id);
      try {
        // Call API to confirm/unconfirm the soda transaction
        const result = await apiService.put<{
          allSodasConfirmed?: boolean;
          trialBalanceConfirmed?: boolean;
        }>(apiRoutes.sodas.confirmSoda(), {
          sodaId: transaction.id,
          isConfirmed: confirmed,
        });
        
        // Update local state only after successful API call
        setData((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            transactions: prev.transactions.map((t) =>
              t.id === transaction.id ? { ...t, confirmed } : t,
            ),
          };
        });
        hasChangesRef.current = true;
        onConfirmationChanged?.({
          gameId: Number(gameId),
          allSodasConfirmed: result?.allSodasConfirmed === true,
          trialBalanceConfirmed: result?.trialBalanceConfirmed,
        });
      } catch (err: unknown) {
        const apiError = err as ApiError;
        const errorMessage = apiError?.error || apiError?.message || 'Unable to update confirmation.';
        setError(errorMessage);
        console.error('Error confirming soda transaction:', err);
        
        // Optionally revert the checkbox state on error
        // The checkbox will remain in its previous state since we don't update state on error
      } finally {
        setUpdatingTransactionId(null);
      }
    },
    [gameId, onConfirmationChanged],
  );

  const effectiveGameType = data?.gameType ?? gameDetails?.gameType;
  // Use actual team names from data, fallback to gameDetails, then to generic labels
  const teamALabel = data?.teamA && data.teamA !== 'Team A' 
    ? data.teamA 
    : gameDetails?.teamA ?? 'Team A';
  const teamBLabel = data?.teamB && data.teamB !== 'Team B' 
    ? data.teamB 
    : gameDetails?.teamB ?? 'Team B';
  const teamCLabel = data?.teamC ?? gameDetails?.teamC;
  const isTestMatch = Boolean(teamCLabel);
  const editableInitialToTeamMap = useMemo(() => {
    return buildInitialToTeamMap({
      gameType: effectiveGameType,
      teamA: teamALabel,
      teamB: teamBLabel,
      teamC: teamCLabel,
    });
  }, [effectiveGameType, teamALabel, teamBLabel, teamCLabel]);

  const editableValidFirstChars = useMemo(() => {
    return buildValidFirstChars({
      gameType: effectiveGameType,
      teamA: teamALabel,
      teamB: teamBLabel,
      teamC: teamCLabel,
    });
  }, [effectiveGameType, teamALabel, teamBLabel, teamCLabel]);

  const editableGameDetails = useMemo(
    () => ({
      gameType: effectiveGameType,
      teamA: teamALabel,
      teamB: teamBLabel,
      teamC: teamCLabel,
    }),
    [effectiveGameType, teamALabel, teamBLabel, teamCLabel],
  );

  const matchDateText = data?.matchDate
    ? ` · ${formatDateOnly(data.matchDate)}`
    : '';

  const title = `${formatGameType(effectiveGameType)} · ${teamALabel} vs ${teamBLabel}${matchDateText}`;

  const sodaEditsLocked = Boolean(gameDetails?.isGameOver) || Boolean(data?.isGameOver);

  const openEditSoda = useCallback(
    (transaction: PartySodaTransactionDetail & { team?: string; isPartnerSoda?: boolean }) => {
      if (!data || sodaEditsLocked || isPartnerUser) return;
      if (transaction.isPartnerSoda) return;
      const parsed = parseLKToRunAndType(transaction.lk || '');
      const lkInitial = transaction.lk?.split('/')?.[0]?.toUpperCase();
      const resolvedTeam =
        transaction.team ||
        (lkInitial ? editableInitialToTeamMap[lkInitial] : undefined) ||
        '';
      const lkForEdit =
        (effectiveGameType ?? '').toLowerCase() === GameType.session
          ? String(transaction.runs ?? parsed.runNumber ?? '')
          : parsed.type || 'L';
      const sodaForEdit: Sodo = {
        id: transaction.id,
        LK: lkForEdit,
        team: resolvedTeam,
        gameId: data.gameId || (typeof gameId === 'number' ? gameId : Number(gameId) || 0),
        amount: transaction.amount,
        rate: transaction.rate,
        party: `${data.partyCode || ''} - ${data.partyName || ''}`.trim(),
        partyId: data.partyId,
        createdAt: new Date().toISOString(),
        user: { id: 0, name: '', username: '' },
        gameType: effectiveGameType || '',
        isPartnerSoda: false,
      };
      setEditingSoda(sodaForEdit);
    },
    [data, effectiveGameType, gameId, editableInitialToTeamMap, sodaEditsLocked, isPartnerUser],
  );

  const isSessionGame = (effectiveGameType ?? '').toLowerCase() === GameType.session;

  const showPartnerEntries = userConfig.ledgerSelfPartnerOriginalOnly !== true;
  const canToggleSelfPartner = isPartnerSoda === undefined && data?.isSelfPartner === true;
  const visibleTransactions = useMemo(() => {
    if (!data) return [];
    if (!canToggleSelfPartner || showPartnerEntries) return data.transactions;
    return data.transactions.filter((transaction) => transaction.isPartnerSoda !== true);
  }, [data, canToggleSelfPartner, showPartnerEntries]);

  const handlePartnerEntriesToggle = async () => {
    if (savingPartnerView) return;
    setSavingPartnerView(true);
    try {
      await updateUserConfig({ ledgerSelfPartnerOriginalOnly: showPartnerEntries });
    } catch {
      // The saved choice stays as it was. AuthContext reports the failure.
    } finally {
      setSavingPartnerView(false);
    }
  };

  const sessionMatchRunEffective = useMemo(() => {
    if (!isSessionGame || !data) return 0;
    const merged = gameDetails?.sessionMatchRun ?? data.sessionMatchRun;
    if (merged !== undefined && merged !== null && Number.isFinite(Number(merged))) {
      return Math.min(MAX_RUN_FOR_SESSION, Math.max(0, Number(merged)));
    }
    const explicitWinner =
      gameDetails?.sessionWinnerTeamName ?? data.sessionWinnerTeamName?.trim();
    const winnerLabel =
      explicitWinner && `${explicitWinner}`.trim()
        ? `${explicitWinner}`.trim()
        : normalizeNumber(data.partyTotalTeamA) >= normalizeNumber(data.partyTotalTeamB)
          ? teamALabel
          : teamBLabel;
    const runsFromWinnerTeam = data.transactions
      .filter(
        (t) =>
          (t.team ?? '').trim() !== '' &&
          winnerLabel &&
          (t.team ?? '').toUpperCase() === winnerLabel.toUpperCase(),
      )
      .map((t) => {
        if (t.runs != null && Number.isFinite(t.runs)) return t.runs;
        const { runNumber } = parseLKToRunAndType(t.lk || '');
        return runNumber;
      });
    if (runsFromWinnerTeam.length) {
      const maxW = Math.max(...runsFromWinnerTeam);
      return Math.min(MAX_RUN_FOR_SESSION, Math.max(0, maxW));
    }
    if (!data.transactions.length) return 0;
    const runsFromRows = data.transactions.map((t) => {
      if (t.runs != null && Number.isFinite(t.runs)) return t.runs;
      const { runNumber } = parseLKToRunAndType(t.lk || '');
      return runNumber;
    });
    const maxR = runsFromRows.length ? Math.max(...runsFromRows) : 0;
    return Math.min(MAX_RUN_FOR_SESSION, Math.max(0, maxR));
  }, [
    isSessionGame,
    data,
    gameDetails?.sessionMatchRun,
    gameDetails?.sessionWinnerTeamName,
    teamALabel,
    teamBLabel,
  ]);

  const sessionWinnerDisplayName = useMemo(() => {
    if (!isSessionGame || !data) return '';
    const explicit = gameDetails?.sessionWinnerTeamName ?? data.sessionWinnerTeamName;
    if (explicit && `${explicit}`.trim()) return `${explicit}`.trim();
    const a = normalizeNumber(data.partyTotalTeamA);
    const b = normalizeNumber(data.partyTotalTeamB);
    return a >= b ? teamALabel : teamBLabel;
  }, [isSessionGame, data, gameDetails?.sessionWinnerTeamName, teamALabel, teamBLabel]);

  const handleDialogClose = useCallback(() => {
    const resolvedGameId =
      data?.gameId ?? (typeof gameId === 'number' ? gameId : parseInt(String(gameId), 10) || 0);
    if (resolvedGameId && hasChangesRef.current) {
      window.dispatchEvent(
        new CustomEvent('party-soda-transactions-changed', {
          detail: { gameId: resolvedGameId },
        }),
      );
    }
    onClose();
  }, [data?.gameId, gameId, onClose]);

  // Helper function to create Sodo-like object from transaction
  // This matches the structure used in GameBoard.tsx
  // Extract just the second part of LK (L or K) to match what calculateSodaAmount expects
  const createSodoLike = (transaction: PartySodaTransactionDetail & { team?: string; commissionSnapshot?: any }) => {
    // Extract the second part of LK (after "/") - should be "L" or "K"
    // The stored transaction.lk is in format "A/L" or "A/K", but calculateSodaAmount expects just "L" or "K"
    // This matches how GameBoard.tsx structures the sodo object
    let lkValue = transaction.lk.toUpperCase();
    // If LK is in format "A/L" or "A/K", extract just the second part (L or K)
    if (lkValue.includes('/')) {
      const parts = lkValue.split('/');
      lkValue = (parts[1] || parts[0] || lkValue).toUpperCase(); // Use second part (L or K)
    }
    // If it's already just "L" or "K", use it as is
    
    // Resolve gameType with better fallback and logging
    const resolvedGameType = data?.gameType || effectiveGameType;
    
    // Log warning if gameType is still missing
    if (!resolvedGameType) {
      console.warn('[createSodoLike] gameType is missing:', {
        transactionId: transaction.id,
        'data?.gameType': data?.gameType,
        effectiveGameType: effectiveGameType,
        gameId: gameId,
        'data?.gameId': data?.gameId,
      });
    }
    
    return {
      id: transaction.id,
      LK: lkValue, // Use just "L" or "K" (not "A/L" or "A/K") to match GameBoard structure
      team: transaction.team || '', // Use stored team from normalizeTransaction
      gameId: data?.gameId || (typeof gameId === 'number' ? gameId : parseInt(gameId as string) || 0),
      amount: transaction.amount,
      rate: transaction.rate,
      party: data?.partyCode || '',
      createdAt: new Date().toISOString(),
      user: { id: 0, name: '', username: '' },
      gameType: resolvedGameType, // May be undefined, but better than empty string
      commissionSnapshot: transaction.commissionSnapshot, // Use stored commissionSnapshot if available
    };
  };

  if (!isOpen) {
    return null;
  }

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={handleDialogClose}
      title={title}
      lockOverlayScroll
      compactChrome
      headerExtra={
        !loading &&
        !error &&
        data && (
          <>
            <span className="font-semibold tabular-nums tracking-wide text-gray-900">{data.partyCode}</span>
            <span className="mx-1 text-gray-500">·</span>
            <span className="font-medium uppercase text-gray-900">{data.partyName}</span>
          </>
        )
      }
      headerAside={
        canToggleSelfPartner && !loading && !error && data ? (
          <label className="inline-flex items-center gap-1.5 text-[11px] font-medium normal-case tracking-normal text-gray-800">
            <span>Show partner entries</span>
            <button
              type="button"
              role="switch"
              aria-checked={showPartnerEntries}
              aria-label="Show partner entries"
              disabled={savingPartnerView}
              onClick={(event) => {
                event.stopPropagation();
                void handlePartnerEntriesToggle();
              }}
              className={`relative h-4 w-7 rounded-full transition-colors ${
                showPartnerEntries ? 'bg-blue-600' : 'bg-slate-400'
              } ${savingPartnerView ? 'opacity-60' : ''}`}
            >
              <span
                className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
                  showPartnerEntries ? 'left-3.5' : 'left-0.5'
                }`}
              />
            </button>
          </label>
        ) : null
      }
      panelClassName="flex max-h-[calc(100dvh-3rem-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] min-h-0 w-auto max-w-[min(100vw,100%)] flex-col overflow-hidden"
    >
      <div className="flex min-h-0 w-[1200px] max-w-[95vw] flex-1 flex-col overflow-hidden rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border border-slate-200 dark:border-slate-600 shadow-[0_8px_30px_rgb(0,0,0,0.12)] text-slate-900 dark:text-slate-100">
        {loading && (
          <div className="flex min-h-[12rem] shrink-0 items-center justify-center text-sm text-slate-600 dark:text-slate-400">
            Loading transactions...
          </div>
        )}
        {!loading && error && (
          <div className="flex min-h-[12rem] shrink-0 items-center justify-center px-6 text-sm text-rose-600 dark:text-rose-400 text-center">
            {error}
          </div>
        )}
        {!loading && !error && data && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col px-3 py-2">
              {isSessionGame && (
                <div className="mb-2 shrink-0 border-b border-slate-200 pb-2 text-xs text-slate-700 dark:border-slate-600 dark:text-slate-300">
                  <span className="text-slate-500 dark:text-slate-400">Winner </span>
                  <span className="font-semibold">{sessionWinnerDisplayName}</span>
                  <span className="mx-3 text-slate-400 dark:text-slate-500">·</span>
                  <span className="text-slate-500 dark:text-slate-400">Runs </span>
                  <span className="font-semibold tabular-nums">{sessionMatchRunEffective}</span>
                </div>
              )}

              <div
                className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] scrollbar-thin scrollbar-thumb-slate-400 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgb(148 163 184) rgba(148, 163, 184, 0.2)',
                }}
              >
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="py-2 px-3 text-right w-12 text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">#</th>
                      <th className="py-2 px-3 text-left w-20 text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">LK</th>
                      <th className="py-2 px-3 text-right text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">Amount</th>
                      <th className="py-2 px-3 text-right text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">Rate</th>
                      <th className="py-2 px-3 text-left whitespace-nowrap text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">Date/Time</th>
                      {isSessionGame ? (
                        <>
                          <th className="py-2 px-3 text-right border-l border-b border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 text-xs font-medium">
                            Win/Loss
                          </th>
                          <th className="py-2 px-2 text-right border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-[10px] font-medium leading-tight">
                            <div>HarCut</div>
                            <div className="font-normal text-slate-500 dark:text-slate-400 truncate max-w-[5rem]" title={teamALabel}>
                              {teamALabel}
                            </div>
                          </th>
                          <th className="py-2 px-2 text-right border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-[10px] font-medium leading-tight">
                            <div>HarCut</div>
                            <div className="font-normal text-slate-500 dark:text-slate-400 truncate max-w-[5rem]" title={teamBLabel}>
                              {teamBLabel}
                            </div>
                          </th>
                          {isTestMatch && teamCLabel && (
                            <th className="py-2 px-2 text-right border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-[10px] font-medium leading-tight">
                              <div>HarCut</div>
                              <div className="font-normal text-slate-500 dark:text-slate-400 truncate max-w-[5rem]" title={teamCLabel}>
                                {teamCLabel}
                              </div>
                            </th>
                          )}
                          <th className="py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-xs font-medium">
                            Running
                          </th>
                        </>
                      ) : (
                        <>
                          <th className="py-2 px-3 text-right border-l border-b border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 text-xs font-medium">{teamALabel}</th>
                          <th className="py-2 px-3 text-right text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">{teamBLabel}</th>
                          {isTestMatch && (
                            <th className="py-2 px-3 text-right text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">{teamCLabel}</th>
                          )}
                          <th className="py-2 px-2 text-right border-l border-b border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 text-[10px] font-medium leading-tight">
                            <div>HarCut</div>
                            <div className="font-normal text-slate-500 dark:text-slate-400 truncate max-w-[5rem]" title={teamALabel}>
                              {teamALabel}
                            </div>
                          </th>
                          <th className="py-2 px-2 text-right border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-[10px] font-medium leading-tight">
                            <div>HarCut</div>
                            <div className="font-normal text-slate-500 dark:text-slate-400 truncate max-w-[5rem]" title={teamBLabel}>
                              {teamBLabel}
                            </div>
                          </th>
                          {isTestMatch && teamCLabel && (
                            <th className="py-2 px-2 text-right border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-[10px] font-medium leading-tight">
                              <div>HarCut</div>
                              <div className="font-normal text-slate-500 dark:text-slate-400 truncate max-w-[5rem]" title={teamCLabel}>
                                {teamCLabel}
                              </div>
                            </th>
                          )}
                          <th className="py-2 px-3 text-right border-l border-b border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 text-xs font-medium">Running {teamALabel}</th>
                          <th className="py-2 px-3 text-right text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">Running {teamBLabel}</th>
                          {isTestMatch && (
                            <th className="py-2 px-3 text-right text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">Running {teamCLabel}</th>
                          )}
                        </>
                      )}
                      {!isPartnerUser && (
                      <th className="py-2 px-3 text-center w-24 text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">Confirmed</th>
                      )}
                      {!isPartnerUser && (
                      <th className="py-2 px-3 text-center w-16 text-slate-600 dark:text-slate-400 text-xs font-medium border-b border-slate-200 dark:border-slate-700">
                        Edit
                      </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTransactions.map((transaction, index) => {
                      const transactionWithTeam = transaction as PartySodaTransactionDetail & {
                        team?: string;
                        commissionSnapshot?: any;
                      };
                      const sodo = createSodoLike(transactionWithTeam);
                      const gidResolved = data.gameId ?? (typeof gameId === 'number' ? gameId : parseInt(String(gameId), 10) || 0);
                      const partyCodeResolved = data.partyCode ?? '';

                      const teamAWinValue = calculateSodaAmount(sodo, teamALabel);
                      const teamBWinValue = calculateSodaAmount(sodo, teamBLabel);
                      const teamCWinValue = isTestMatch && teamCLabel ? calculateSodaAmount(sodo, teamCLabel) : undefined;

                      const teamAWinBeforeCommission = calculateSodaAmountBeforeCommission(sodo, teamALabel);
                      const teamBWinBeforeCommission = calculateSodaAmountBeforeCommission(sodo, teamBLabel);
                      const teamCWinBeforeCommission =
                        isTestMatch && teamCLabel
                          ? calculateSodaAmountBeforeCommission(sodo, teamCLabel)
                          : undefined;

                      const skipHarCutPartnerChild = isPartnerSodaChildRow(transaction);

                      const prevTransactions = visibleTransactions.slice(0, index);

                      const sessionRowWinLoss = isSessionGame
                        ? sessionWinLossAtMatchRun(
                            transaction,
                            sessionMatchRunEffective,
                            gidResolved,
                            partyCodeResolved,
                            effectiveGameType,
                            partnerSodaFlag,
                          )
                        : 0;

                      const sessionRunningValue = isSessionGame
                        ? prevTransactions.reduce(
                            (sum, t) =>
                              sum +
                              sessionWinLossAtMatchRun(
                                t,
                                sessionMatchRunEffective,
                                gidResolved,
                                partyCodeResolved,
                                effectiveGameType,
                                partnerSodaFlag,
                              ),
                            0,
                          ) + sessionRowWinLoss
                        : 0;

                      const runningTeamAValue = !isSessionGame
                        ? prevTransactions.reduce((sum, t) => {
                            const prevSodo = createSodoLike(
                              t as PartySodaTransactionDetail & { team?: string; commissionSnapshot?: any },
                            );
                            return sum + calculateSodaAmount(prevSodo, teamALabel);
                          }, 0) + teamAWinValue
                        : 0;

                      const runningTeamBValue = !isSessionGame
                        ? prevTransactions.reduce((sum, t) => {
                            const prevSodo = createSodoLike(
                              t as PartySodaTransactionDetail & { team?: string; commissionSnapshot?: any },
                            );
                            return sum + calculateSodaAmount(prevSodo, teamBLabel);
                          }, 0) + teamBWinValue
                        : 0;

                      const runningTeamCValue =
                        !isSessionGame && isTestMatch && teamCLabel && teamCWinValue !== undefined
                          ? prevTransactions.reduce((sum, t) => {
                              const prevSodo = createSodoLike(
                                t as PartySodaTransactionDetail & { team?: string; commissionSnapshot?: any },
                              );
                              return sum + calculateSodaAmount(prevSodo, teamCLabel!);
                            }, 0) + teamCWinValue
                          : undefined;

                      const lkHintClass = (() => {
                        const { type } = parseLKToRunAndType(transaction.lk || '');
                        if (type === 'K') return isPartnerSoda ? 'text-rose-500/90 dark:text-rose-300' : 'text-rose-600 dark:text-rose-400';
                        if (type === 'L') return isPartnerSoda ? 'text-emerald-600/90 dark:text-emerald-300' : 'text-emerald-600 dark:text-emerald-400';
                        return 'text-slate-900 dark:text-slate-100';
                      })();

                      const displaySequence = transaction.gameWiseSodaSequence;

                      return (
                        <tr
                          key={transaction.id}
                          className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                        >
                          <td className="py-2 px-3 text-right text-xs text-slate-600 dark:text-slate-400 border-r border-slate-200 dark:border-slate-700">
                            {displaySequence}
                          </td>
                          <td className={`py-2 px-3 text-left font-semibold ${lkHintClass}`}>
                            {formatLkColumnValue(transaction.lk, effectiveGameType, transaction.runs)}
                          </td>
                          <td className="py-2 px-3 text-right text-slate-900 dark:text-slate-100">
                            <span>{formatAmount(transaction.amount)}</span>
                            <span className="font-bold mx-1">×</span>
                          </td>
                          <td className="py-2 px-3 text-right text-slate-900 dark:text-slate-100">
                            {formatAmount(transaction.rate)}
                          </td>
                          <td className="py-2 px-3 text-left whitespace-nowrap text-xs text-slate-700 dark:text-slate-300">
                            {transaction.createdAt ? formatDate(transaction.createdAt) : '—'}
                          </td>
                          {isSessionGame ? (
                            <>
                              <td className={`py-2 px-3 text-right border-l border-slate-200 dark:border-slate-600 ${getNetClass(sessionRowWinLoss)}`}>
                                {formatAmount(sessionRowWinLoss)}
                              </td>
                              <td
                                className={`py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 ${harCutCellClass(
                                  transaction.amount,
                                  data.harCut,
                                  teamAWinBeforeCommission,
                                  skipHarCutPartnerChild,
                                )}`}
                              >
                                {formatHarCutCell(
                                  transaction.amount,
                                  data.harCut,
                                  teamAWinBeforeCommission,
                                  skipHarCutPartnerChild,
                                )}
                              </td>
                              <td
                                className={`py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 ${harCutCellClass(
                                  transaction.amount,
                                  data.harCut,
                                  teamBWinBeforeCommission,
                                  skipHarCutPartnerChild,
                                )}`}
                              >
                                {formatHarCutCell(
                                  transaction.amount,
                                  data.harCut,
                                  teamBWinBeforeCommission,
                                  skipHarCutPartnerChild,
                                )}
                              </td>
                              {isTestMatch && teamCLabel && (
                                <td
                                  className={`py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 ${harCutCellClass(
                                    transaction.amount,
                                    data.harCut,
                                    teamCWinBeforeCommission ?? 0,
                                    skipHarCutPartnerChild,
                                  )}`}
                                >
                                  {formatHarCutCell(
                                    transaction.amount,
                                    data.harCut,
                                    teamCWinBeforeCommission ?? 0,
                                    skipHarCutPartnerChild,
                                  )}
                                </td>
                              )}
                              <td className={`py-2 px-3 text-right ${getNetClass(sessionRunningValue)}`}>
                                {formatAmount(sessionRunningValue)}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className={`py-2 px-3 text-right border-l border-slate-200 dark:border-slate-600 ${getNetClass(teamAWinValue)}`}>
                                {formatAmount(teamAWinValue)}
                              </td>
                              <td className={`py-2 px-3 text-right ${getNetClass(teamBWinValue)}`}>
                                {formatAmount(teamBWinValue)}
                              </td>
                              {isTestMatch && teamCLabel && (
                                <td className={`py-2 px-3 text-right ${getNetClass(teamCWinValue)}`}>
                                  {formatAmount(teamCWinValue ?? 0)}
                                </td>
                              )}
                              <td
                                className={`py-2 px-3 text-right border-l border-slate-200 dark:border-slate-600 ${harCutCellClass(
                                  transaction.amount,
                                  data.harCut,
                                  teamAWinBeforeCommission,
                                  skipHarCutPartnerChild,
                                )}`}
                              >
                                {formatHarCutCell(
                                  transaction.amount,
                                  data.harCut,
                                  teamAWinBeforeCommission,
                                  skipHarCutPartnerChild,
                                )}
                              </td>
                              <td
                                className={`py-2 px-3 text-right ${harCutCellClass(
                                  transaction.amount,
                                  data.harCut,
                                  teamBWinBeforeCommission,
                                  skipHarCutPartnerChild,
                                )}`}
                              >
                                {formatHarCutCell(
                                  transaction.amount,
                                  data.harCut,
                                  teamBWinBeforeCommission,
                                  skipHarCutPartnerChild,
                                )}
                              </td>
                              {isTestMatch && teamCLabel && (
                                <td
                                  className={`py-2 px-3 text-right ${harCutCellClass(
                                    transaction.amount,
                                    data.harCut,
                                    teamCWinBeforeCommission ?? 0,
                                    skipHarCutPartnerChild,
                                  )}`}
                                >
                                  {formatHarCutCell(
                                    transaction.amount,
                                    data.harCut,
                                    teamCWinBeforeCommission ?? 0,
                                    skipHarCutPartnerChild,
                                  )}
                                </td>
                              )}
                              <td className={`py-2 px-3 text-right border-l border-slate-200 dark:border-slate-600 ${getNetClass(runningTeamAValue)}`}>
                                {formatAmount(runningTeamAValue)}
                              </td>
                              <td className={`py-2 px-3 text-right ${getNetClass(runningTeamBValue)}`}>
                                {formatAmount(runningTeamBValue)}
                              </td>
                              {isTestMatch && (
                                <td className={`py-2 px-3 text-right ${getNetClass(runningTeamCValue)}`}>
                                  {formatAmount(runningTeamCValue ?? 0)}
                                </td>
                              )}
                            </>
                          )}
                          {!isPartnerUser && (
                          <td className="py-2 px-3 text-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 cursor-pointer accent-blue-600 disabled:opacity-60"
                              checked={transaction.confirmed ?? false}
                              disabled={updatingTransactionId === transaction.id}
                              onChange={(event) => handleConfirmToggle(transaction, event.target.checked)}
                            />
                          </td>
                          )}
                          {!isPartnerUser && (
                          <td className="py-2 px-3 text-center">
                            {!sodaEditsLocked && !transaction.isPartnerSoda && !isPartnerSodaChildRow(transaction) ? (
                              <button
                                type="button"
                                onClick={() =>
                                  openEditSoda(
                                    transaction as PartySodaTransactionDetail & {
                                      team?: string;
                                      isPartnerSoda?: boolean;
                                    },
                                  )
                                }
                                className="inline-flex items-center justify-center rounded p-1 text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700"
                                title="Edit soda"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            ) : null}
                          </td>
                          )}
                        </tr>
                      );
                    })}
                    {visibleTransactions.length === 0 && (
                      <tr>
                        <td
                          colSpan={
                            (isSessionGame ? (isTestMatch ? 12 : 11) : isTestMatch ? 16 : 13) -
                            (isPartnerUser ? 2 : 0)
                          }
                          className="py-6 text-center text-sm text-slate-500 dark:text-slate-400"
                        >
                          No transactions recorded for this party.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {visibleTransactions.length > 0 &&
                    (isSessionGame
                      ? (() => {
                          const gidResolved = data.gameId ?? (typeof gameId === 'number' ? gameId : parseInt(String(gameId), 10) || 0);
                          const partyCodeResolved = data.partyCode ?? '';
                          const totalSessionWinLoss = visibleTransactions.reduce(
                            (sum, t) =>
                              sum +
                              sessionWinLossAtMatchRun(
                                t,
                                sessionMatchRunEffective,
                                gidResolved,
                                partyCodeResolved,
                                effectiveGameType,
                                partnerSodaFlag,
                              ),
                            0,
                          );
                          const finalRunning = totalSessionWinLoss;
                          const phc = data.harCut ?? 0;
                          const sumHarCut = (teamLabel: string) => {
                            if (phc === 0) return '—';
                            const s = visibleTransactions.reduce((acc, t) => {
                              if (isPartnerSodaChildRow(t)) return acc;
                              const sodo = createSodoLike(
                                t as PartySodaTransactionDetail & { team?: string; commissionSnapshot?: unknown },
                              );
                              const w = calculateSodaAmountBeforeCommission(sodo, teamLabel);
                              if (w <= 0) return acc;
                              return acc + (t.amount * phc) / 100000;
                            }, 0);
                            return formatAmount(s);
                          };
                          return (
                            <tfoot className="sticky bottom-0 bg-slate-100 dark:bg-slate-800 border-t-2 border-slate-200 dark:border-slate-600">
                              <tr className="text-sm font-semibold">
                                <td
                                  colSpan={5}
                                  className="py-2 px-3 text-left text-slate-800 dark:text-slate-100 border-b border-slate-200 dark:border-slate-700"
                                >
                                  Total
                                </td>
                                <td
                                  className={`py-2 px-3 text-right border-l border-b border-slate-200 dark:border-slate-600 ${getNetClass(totalSessionWinLoss)}`}
                                >
                                  {formatAmount(totalSessionWinLoss)}
                                </td>
                                <td className="py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                                  {sumHarCut(teamALabel)}
                                </td>
                                <td className="py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                                  {sumHarCut(teamBLabel)}
                                </td>
                                {isTestMatch && teamCLabel && (
                                  <td className="py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                                    {sumHarCut(teamCLabel)}
                                  </td>
                                )}
                                <td
                                  className={`py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 ${getNetClass(finalRunning)}`}
                                >
                                  {formatAmount(finalRunning)}
                                </td>
                                {!isPartnerUser && (
                                <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-700" />
                                )}
                                {!isPartnerUser && (
                                <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-700" />
                                )}
                              </tr>
                            </tfoot>
                          );
                        })()
                      : (() => {
                          const calculatedTotalTeamA = visibleTransactions.reduce((sum, t) => {
                            const sodo = createSodoLike(
                              t as PartySodaTransactionDetail & { team?: string; commissionSnapshot?: any },
                            );
                            return sum + calculateSodaAmount(sodo, teamALabel);
                          }, 0);
                          const calculatedTotalTeamB = visibleTransactions.reduce((sum, t) => {
                            const sodo = createSodoLike(
                              t as PartySodaTransactionDetail & { team?: string; commissionSnapshot?: any },
                            );
                            return sum + calculateSodaAmount(sodo, teamBLabel);
                          }, 0);
                          const calculatedTotalTeamC =
                            isTestMatch && teamCLabel
                              ? visibleTransactions.reduce((sum, t) => {
                                  const sodo = createSodoLike(
                                    t as PartySodaTransactionDetail & { team?: string; commissionSnapshot?: any },
                                  );
                                  return sum + calculateSodaAmount(sodo, teamCLabel!);
                                }, 0)
                              : undefined;

                          const partnerEntriesHidden = canToggleSelfPartner && !showPartnerEntries;
                          const storedTotalTeamA =
                            data.totals?.teamA && data.totals.teamA !== 0 ? data.totals.teamA : calculatedTotalTeamA;
                          const storedTotalTeamB =
                            data.totals?.teamB && data.totals.teamB !== 0 ? data.totals.teamB : calculatedTotalTeamB;
                          const storedTotalTeamC =
                            data.totals?.teamC && data.totals.teamC !== 0 ? data.totals.teamC : calculatedTotalTeamC;
                          const totalTeamA = partnerEntriesHidden ? calculatedTotalTeamA : storedTotalTeamA / 100;
                          const totalTeamB = partnerEntriesHidden ? calculatedTotalTeamB : storedTotalTeamB / 100;
                          const totalTeamC = isTestMatch
                            ? partnerEntriesHidden
                              ? calculatedTotalTeamC
                              : (storedTotalTeamC ?? 0) / 100
                            : undefined;

                          const phcOd = data.harCut ?? 0;
                          const sumHarCutOd = (teamLabel: string) => {
                            if (phcOd === 0) return '—';
                            const s = visibleTransactions.reduce((acc, t) => {
                              if (isPartnerSodaChildRow(t)) return acc;
                              const sodo = createSodoLike(
                                t as PartySodaTransactionDetail & { team?: string; commissionSnapshot?: unknown },
                              );
                              const w = calculateSodaAmountBeforeCommission(sodo, teamLabel);
                              if (w <= 0) return acc;
                              return acc + (t.amount * phcOd) / 100000;
                            }, 0);
                            return formatAmount(s);
                          };

                          return (
                            <tfoot className="sticky bottom-0 bg-slate-100 dark:bg-slate-800 border-t-2 border-slate-200 dark:border-slate-600">
                              <tr className="text-sm font-semibold">
                                <td
                                  colSpan={5}
                                  className="py-2 px-3 text-left text-slate-800 dark:text-slate-100 border-b border-slate-200 dark:border-slate-700"
                                >
                                  Total
                                </td>
                                <td
                                  className={`py-2 px-3 text-right border-l border-b border-slate-200 dark:border-slate-600 ${getNetClass(totalTeamA)}`}
                                >
                                  {formatAmount(totalTeamA)}
                                </td>
                                <td
                                  className={`py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 ${getNetClass(totalTeamB)}`}
                                >
                                  {formatAmount(totalTeamB)}
                                </td>
                                {isTestMatch && (
                                  <td
                                    className={`py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 ${getNetClass(totalTeamC)}`}
                                  >
                                    {formatAmount(totalTeamC ?? 0)}
                                  </td>
                                )}
                                <td className="py-2 px-3 text-right border-l border-b border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200">
                                  {sumHarCutOd(teamALabel)}
                                </td>
                                <td className="py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                                  {sumHarCutOd(teamBLabel)}
                                </td>
                                {isTestMatch && teamCLabel && (
                                  <td className="py-2 px-3 text-right border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                                    {sumHarCutOd(teamCLabel)}
                                  </td>
                                )}
                                <td
                                  colSpan={(isTestMatch ? 5 : 4) - (isPartnerUser ? 2 : 0)}
                                  className="py-2 px-3 border-b border-slate-200 dark:border-slate-700"
                                />
                              </tr>
                            </tfoot>
                          );
                        })())}
                </table>
              </div>
            </div>
            <div className="shrink-0 border-t border-slate-200 bg-slate-50/90 px-3 py-1 dark:border-slate-600 dark:bg-slate-900/90">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-[11px] leading-snug text-slate-600 dark:text-slate-400">
                <span className="min-w-0">
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {visibleTransactions.length} transaction{visibleTransactions.length === 1 ? '' : 's'}
                  </span>
                  <span className="text-slate-400 dark:text-slate-500"> · </span>
                  <span className="tabular-nums">Game #{data.gameId}</span>
                  <span className="text-slate-400 dark:text-slate-500"> · </span>
                  <span
                    className="truncate"
                    title={
                      data.teamC
                        ? `${data.teamA} vs ${data.teamB} vs ${data.teamC}`
                        : `${data.teamA} vs ${data.teamB}`
                    }
                  >
                    {data.teamA} vs {data.teamB}
                    {data.teamC ? ` vs ${data.teamC}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-slate-500 dark:text-slate-500">Esc · outside</span>
              </div>
            </div>
            {editingSoda && !isPartnerUser && (
              <div className="shrink-0 border-t border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <LagayaaKhadhaComponent
                  validFirsChar={editableValidFirstChars}
                  onComplete={() => {}}
                  gameId={String(data.gameId || gameId)}
                  parties={parties}
                  gameType={effectiveGameType || ''}
                  teamA={teamALabel}
                  teamB={teamBLabel}
                  teamC={teamCLabel}
                  initialToTeamMap={editableInitialToTeamMap}
                  partnershipEnabled={true}
                  commissionEnabled={true}
                  isGameOver={false}
                  gameDetails={editableGameDetails}
                  editingSoda={editingSoda}
                  editOnly
                  onEditComplete={() => {
                    hasChangesRef.current = true;
                    setEditingSoda(null);
                    loadData();
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </DialogBox>
  );
};

export default PartySodaTransactionsDialog;

